import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanel,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSubtitle,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const emptyForm = {
  unit_id: '',
  base_unit_name: '',
  purchase_unit_name: '',
  status: 'Active'
};

const UnitMeasurementMaster = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/unit-measurements/all');
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/master/unit-measurements/latest-id');
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, unit_id: res.data.nextId }));
      }
    } catch (_) {}
  };

  useEffect(() => { fetchItems(); }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.unit_id || a._id || '');
      const idB = String(b.unit_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const unitId = (item.unit_id || '').toLowerCase();
      const baseUnit = (item.base_unit_name || '').toLowerCase();
      const purchaseUnit = (item.purchase_unit_name || '').toLowerCase();

      return unitId.startsWith(q) || baseUnit.startsWith(q) || purchaseUnit.startsWith(q);
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, items.length, pageSize]);

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({ unit_id: item.unit_id, base_unit_name: item.base_unit_name ,purchase_unit_name: item.purchase_unit_name || '',status: item.status || 'Active'});
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = { ...formData, id: editId };
      await api.post('/master/unit-measurements/upsert', payload);
      toast.success(editId ? 'Updated' : 'Saved');
      setEditId(null);
      setFormData(emptyForm);
      setShowForm(true);
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.message || 'Process failed';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unit?')) return;
    try {
      await api.delete(`/master/unit-measurements/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div>
            <h1 className={masterTitle}>Unit Measurement Master</h1>
            <p className={masterSubtitle}>Quantity Standards</p>
          </div>
        </div>

        {!showForm ? (
          <button
            onClick={async () => {
              setEditId(null);
              setFormData(emptyForm);
              setShowForm(true);
              await fetchNextId();
            }}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add Unit
          </button>
        ) : (
          <button
            onClick={() => setShowForm(false)}
            className={masterPrimaryButton}
          >
            <ArrowLeft size={14} /> VIEW LIST
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h4 className="text-sm font-bold text-slate-700 uppercase">{editId ? 'Edit Unit' : 'New Unit'}</h4>
              <button
                type="button"
                onClick={async () => {
                  setFormData(emptyForm);
                  await fetchNextId();
                }}
                className="text-[10px] font-bold text-rose-500 flex items-center gap-1 uppercase hover:underline"
              >
                <RotateCcw size={12}/> Reset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-1 gap-y-1">
              {/* <Input label="Unit ID" name="unit_id" value={formData.unit_id} readOnly /> */}
              <Input label="Base Unit" name="base_unit_name" value={formData.base_unit_name} onChange={handleChange} required />
              <Input label="Purchase Unit" name="purchase_unit_name" value={formData.purchase_unit_name} onChange={handleChange} />
              <div className="flex flex-col">
                <label className={masterInputLabel}>Status</label>
                <select 
                  name="status" 
                  value={formData.status} 
                  onChange={handleChange} 
                  className={masterInput}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
                {editId ? 'Update' : 'Save'} Unit
              </button>
            </div>
          </form>
        ) : (
          <div className={masterPanel}>
            <div >
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by unit ID or name..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

              <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Base Unit</th>
                    <th className={masterTableData}>Purchase Unit</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {loading ? (
                    <tr><td colSpan="4" className="p-12 text-center">Syncing Data...</td></tr>
                  ) : paginatedData.length > 0 ? paginatedData.map((item, index) => (
                    <tr key={item._id} className={masterTableData}>
                      <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                      <td className={masterTableData}>{item.unit_id}</td>
                      <td className={masterTableData} title={item.base_unit_name}>
                        {item.base_unit_name}
                      </td>
                      <td className={masterTableData} title={item.purchase_unit_name}>
                        {item.purchase_unit_name || 'N/A'}
                      </td>
                      <td className={masterTableData}>
                          <span className={`inline-block px-2 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                            item.status === 'Active' 
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' 
                              : 'bg-rose-100 text-rose-700 border border-rose-300'
                          }`}>
                            {item.status || 'Active'}
                          </span>
                        </td>
                      <td className={masterTableData}>
                        <div className="flex justify-center gap-2">
                          <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-600 rounded-lg hover:bg-rose-100"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No matching records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

export default UnitMeasurementMaster;
